// ============================================================
// SENTRY — frontend error monitoring
// ============================================================
// No-op when VITE_SENTRY_DSN is not set (demo mode, local dev).
// When set, initializes @sentry/react with:
//   - Environment tag (dev / staging / prod)
//   - Release tag (git SHA at build time, optional)
//   - Runtime-mode tag (demo / production / hybrid)
//   - PII scrubber (see beforeSend below)
//   - tracesSampleRate 0.1 in prod, 1.0 in dev
//
// Compliance posture:
//   - We do NOT send Starlink credentials, Stripe keys, MFA codes,
//     account numbers, emails, or anything matching the
//     BLOCKED_PRICING_FIELDS list in lib/compliance.js.
//   - User context is limited to the Cognito `sub` (a UUID) — no
//     email, no PII. Sentry's "user" tab will show only the sub
//     so we can correlate a crash with a session without exposing
//     PII to whoever's reading Sentry.

import * as Sentry from '@sentry/react';
import { getRuntimeMode } from './runtime-mode.js';

const DSN = import.meta.env?.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env?.VITE_SENTRY_ENVIRONMENT || (import.meta.env?.DEV ? 'dev' : 'prod');
const RELEASE = import.meta.env?.VITE_SENTRY_RELEASE; // e.g. git SHA injected at build time

// Field names we MUST strip from any payload (request body, breadcrumb, extra context)
// Mirrors BLOCKED_PRICING_FIELDS in compliance.js but adds auth/PII fields specific to
// frontend telemetry.
const FORBIDDEN_KEYS = new Set([
  // Auth / credential material
  'password', 'passwd', 'pwd', 'secret', 'token', 'access_token', 'refresh_token',
  'id_token', 'jwt', 'authorization', 'cookie', 'session', 'mfa_code', 'mfaCode',
  'totp', 'totpCode', 'otp',
  // Payment material (out of PCI scope — must never appear here)
  'cardNumber', 'card_number', 'cvc', 'cvv', 'cardCvc', 'stripeSecret', 'stripeSecretKey',
  // Internal pricing (ASP § 3.2(a))
  'cost', 'wholesale', 'wholesalePrice', 'margin', 'marginPercent', 'internalPrice',
  'supplierCost', 'supplierName', 'commission',
  // Starlink credentials (ASP § 10)
  'clientSecret', 'starlinkClientSecret',
]);

const SCRUB_PLACEHOLDER = '[Filtered]';

/**
 * Walk an object and replace any forbidden-key value with [Filtered].
 * Used by Sentry's beforeSend hook.
 */
function scrub(obj, depth = 0) {
  if (depth > 8) return obj; // depth limit so we don't blow the stack
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((v) => scrub(v, depth + 1));
  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    if (FORBIDDEN_KEYS.has(key)) {
      out[key] = SCRUB_PLACEHOLDER;
    } else if (typeof value === 'object' && value !== null) {
      out[key] = scrub(value, depth + 1);
    } else {
      out[key] = value;
    }
  }
  return out;
}

let initialized = false;

export function initSentry() {
  if (initialized) return;
  initialized = true;

  if (!DSN) {
    // No DSN configured — Sentry stays a no-op so demo builds don't ping
    // a fake project. captureException calls below are still safe to make.
    if (import.meta.env?.DEV) {
      console.info('[sentry] VITE_SENTRY_DSN not set — error reporting disabled');
    }
    return;
  }

  const mode = getRuntimeMode();

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    // Conservative sampling — bump if alerts are too quiet
    tracesSampleRate: ENVIRONMENT === 'prod' ? 0.1 : 1.0,
    // Session Replay — disabled by default for compliance review.
    // When enabled, mask all text/inputs to avoid leaking PII.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Don't send default PII — IP address, full URL, etc.
    sendDefaultPii: false,
    initialScope: {
      tags: {
        runtime_mode: mode.isProduction ? 'production' : mode.isDemo ? 'demo' : 'hybrid',
        real_auth: String(mode.realAuth),
        real_data: String(mode.realData),
        real_payments: String(mode.realPayments),
      },
    },
    beforeSend(event) {
      // Scrub the full event before send. Sentry's own scrubber catches
      // some of this but our list is stricter (Starlink + ASP compliance).
      if (event.request) event.request = scrub(event.request);
      if (event.extra) event.extra = scrub(event.extra);
      if (event.contexts) event.contexts = scrub(event.contexts);
      if (event.tags) event.tags = scrub(event.tags);
      // Strip email from user object; keep `id` (Cognito sub UUID) for correlation
      if (event.user) {
        const { id } = event.user;
        event.user = id ? { id } : undefined;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) breadcrumb.data = scrub(breadcrumb.data);
      return breadcrumb;
    },
  });
}

/**
 * Set the current user for crash attribution. Pass the Cognito sub only —
 * no email, no display name.
 */
export function setSentryUser(sub) {
  if (!DSN) return;
  if (sub) Sentry.setUser({ id: sub });
  else Sentry.setUser(null);
}

/**
 * Manually report an error (for catch blocks where you want to log + continue).
 */
export function captureError(err, context) {
  if (!DSN) {
    if (import.meta.env?.DEV) console.error('[captureError]', err, context);
    return;
  }
  Sentry.captureException(err, context ? { extra: scrub(context) } : undefined);
}

/**
 * Manually report a message (warning, info breadcrumb).
 */
export function captureMessage(message, level = 'info') {
  if (!DSN) return;
  Sentry.captureMessage(message, level);
}

export { Sentry };
